/**
 * Busca das mensagens rápidas no "/" do composer. O atendente digita "/" e uma
 * palavra; vale qualquer pedaço do atalho (não só o começo) e do texto, sem
 * diferença de acento nem de maiúscula — "/entrega" acha "/taxa-entrega" e
 * também "Sua entrega sai hoje".
 *
 * Ordem: atalho que COMEÇA com a palavra, depois atalho que a CONTÉM, depois
 * quem só tem a palavra no texto. Dentro de cada grau, a ordem do servidor
 * (minhas → empresa) se mantém.
 */
import type { QuickReply } from '../types';

type Rapida = Pick<QuickReply, 'shortcut' | 'body'>;

/** Forma de comparação: sem acento, minúsculo. */
function formaDeComparacao(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** O que o atendente digitou depois da barra ("/Entrega" → "entrega"). */
export function termoDaBusca(digitado: string): string {
  return formaDeComparacao(digitado.replace(/^\/+/, '').trim());
}

/**
 * Grau do casamento: 0 = atalho começa com o termo, 1 = atalho contém,
 * 2 = só o texto contém; null = não casa. Termo vazio casa tudo no grau 0.
 */
export function grauDaRapida(q: Rapida, termo: string): number | null {
  if (!termo) return 0;
  const atalho = formaDeComparacao(q.shortcut.replace(/^\/+/, ''));
  if (atalho.startsWith(termo)) return 0;
  if (atalho.includes(termo)) return 1;
  if (formaDeComparacao(q.body).includes(termo)) return 2;
  return null;
}

/** Filtra e ordena pelo grau (sort estável: empate mantém a ordem recebida). */
export function filtrarRapidas<T extends Rapida>(lista: T[], digitado: string): T[] {
  const termo = termoDaBusca(digitado);
  return lista
    .map((q) => ({ q, grau: grauDaRapida(q, termo) }))
    .filter((x): x is { q: T; grau: number } => x.grau !== null)
    .sort((a, b) => a.grau - b.grau)
    .map((x) => x.q);
}
